import Axios from '../utils/Axios';

export interface Resource {
  type: string;
  id: string;
  attributes?: Record<string, any>;
  relationships?: Record<string, { data?: Resource | Resource[] | null }>;
  meta?: Record<string, any>;
}
export interface Document {
  data: Resource[] | Resource;
  included?: Resource[];
  meta?: { pagination: { page: number; pages: number; count: number } };
}
export interface Officer {
  id: string;
  firstname: string;
  lastname: string;
  profile_headshot?: string;
  managerId?: string;
  teamName?: string;
  status?: string;
}
export interface Query {
  page?: number;
  size?: number;
  keyword?: string;
  status?: string;
  type?: string;
  sort?: string;
  order?: string;
}
export type SelectionDeal = Pick<Deal, 'id' | 'is_loan_officer_reassignable'>;
const base = '/internal/v1';
const relationship = (r: Resource, key: string) =>
  r.relationships?.[key]?.data as Resource | null | undefined;
const related = (r: Resource, key: string, included: Resource[]) => {
  const ref = relationship(r, key);
  return included.find(
    (item) => item.type === ref?.type && item.id === ref?.id,
  );
};
export function mapOfficer(r: Resource, included: Resource[] = []): Officer {
  return {
    ...r.attributes,
    id: r.id,
    firstname: r.attributes?.firstname || '',
    lastname: r.attributes?.lastname || '',
    managerId: relationship(r, 'manager')?.id,
    teamName: related(r, 'team', included)?.attributes?.name,
  };
}
export function mapConnection(r: Resource, included: Resource[]): Connection {
  const agent = related(r, 'agent', included);
  return {
    ...r.attributes,
    id: r.id,
    stage_id: Number(relationship(r, 'stage')?.id),
    agent: agent
      ? { ...agent.attributes, id: agent.id, vid: agent.id }
      : undefined,
  } as unknown as Connection;
}
export function mapDeal(r: Resource, included: Resource[] = []): Deal {
  const customer = related(r, 'customer', included);
  const location = related(r, 'location', included);
  const officer = related(r, 'loan_officer', included);
  const connections = (r.relationships?.connections?.data || []) as Resource[];
  return {
    ...r.attributes,
    id: r.id,
    transaction_type: r.attributes?.dealtype,
    updated_at: r.attributes?.last_update_at,
    hubspot_dealstage_id: Number(relationship(r, 'hubspot_dealstage')?.id),
    customer: {
      ...customer?.attributes,
      id: customer?.id,
      phone: customer ? relationship(customer, 'phone')?.id : undefined,
    },
    location: location
      ? {
          ...location.attributes,
          zipcode: location.attributes?.zipcode
            ? {
                zipcode: location.attributes.zipcode,
                state: location.attributes.state,
              }
            : null,
        }
      : null,
    loan_officer: officer
      ? { ...officer.attributes, id: officer.id }
      : undefined,
    connections: connections
      .map((ref) =>
        included.find((item) => item.type === ref.type && item.id === ref.id),
      )
      .filter(Boolean)
      .map((item) => mapConnection(item!, included)),
  } as unknown as Deal;
}
export function dealQuery(q: Query): URLSearchParams {
  const params = new URLSearchParams({
    'page[number]': String(q.page || 1),
    'page[size]': String(q.size || 10),
  });
  if (q.keyword) {
    params.set('filter[keyword]', q.keyword);
  }
  if (q.status) {
    params.set('filter[status_group]', q.status);
  }
  if (q.type) {
    params.set(
      'filter[dealtype]',
      ({ buyer: 'Buy', seller: 'Sell' } as Record<string, string>)[q.type] ||
        q.type,
    );
  }
  if (q.sort) {
    params.set(
      'sort',
      `${q.order === 'asc' ? '' : '-'}${
        q.sort === 'create-date' ? 'created_at' : 'last_update_at'
      }`,
    );
  }
  return params;
}
export function apiMessage(error: any): string {
  const errors = error?.response?.data?.errors;
  if (errors) {
    const values = Array.isArray(errors) ? errors : Object.values(errors);
    return (
      values
        .map((v: any) => {
          if (typeof v === 'string') {
            return v;
          }
          if (Array.isArray(v)) {
            return v.join(', ');
          }
          return v?.detail || '';
        })
        .filter(Boolean)
        .join(', ') || 'Request failed. Please try again.'
    );
  }
  return error?.message || 'Request failed. Please try again.';
}
export function reportKeyword(
  keyword: string,
  officer: Partial<Officer>,
): string {
  const term = keyword.toLowerCase();
  return `${officer.firstname} ${officer.lastname}`
    .toLowerCase()
    .includes(term) || officer.teamName?.toLowerCase().includes(term)
    ? ''
    : keyword;
}
// eslint-disable-next-line max-params
export function mergeDeals(
  own: Deal[],
  reports: Deal[],
  sort: string,
  order: string,
  page: number,
  size = 10,
): Deal[] {
  const key = sort === 'create-date' ? 'created_at' : 'updated_at';
  return [...own, ...reports]
    .sort((a, b) => {
      const diff = new Date(a[key]).getTime() - new Date(b[key]).getTime();
      return (
        (order === 'asc' ? diff : -diff) ||
        String(a.id).localeCompare(String(b.id))
      );
    })
    .slice((page - 1) * size, page * size);
}
export function selectionAfterToggle(
  current: string[],
  deals: SelectionDeal[],
  checked: boolean,
): string[] {
  const eligible = deals
    .filter((d) => d.is_loan_officer_reassignable)
    .map((d) => String(d.id));
  const next = checked
    ? Array.from(new Set([...current, ...eligible]))
    : current.filter((id) => !eligible.includes(id));
  if (next.length > 500) {
    throw new Error('Select at most 500 deals at once.');
  }
  return next;
}
export async function getDocument(
  path: string,
  params?: URLSearchParams,
  signal?: AbortSignal,
): Promise<Document> {
  if (signal?.aborted) {
    throw new DOMException('Request cancelled', 'AbortError');
  }
  const query = params?.toString();
  const url = `${base}${path}${query ? `?${query}` : ''}`;
  return (await (signal ? Axios.get(url, { signal }) : Axios.get(url))).data;
}
export async function getDeals(id: string, q: Query, reports = false) {
  const params = dealQuery(q);
  params.set(
    'include',
    'customer,location,loan_officer,hubspot_dealstage,connections,connections.agent,connections.stage',
  );
  const doc = await getDocument(
    `/loan-officers/${id}/${reports ? 'direct-reports/' : ''}deals/`,
    params,
  );
  return {
    results: (doc.data as Resource[]).map((r) => mapDeal(r, doc.included)),
    count: doc.meta!.pagination.count,
  };
}
export async function dealPrefix(
  id: string,
  q: Query,
  limit: number,
  reports = false,
) {
  let results: Deal[] = [];
  let count = 0;
  for (let page = 1; ; page += 1) {
    // eslint-disable-next-line no-await-in-loop
    const batch = await getDeals(
      id,
      { ...q, page, size: Math.min(100, limit) },
      reports,
    );
    results = [...results, ...batch.results];
    count = batch.count;
    if (results.length >= Math.min(count, limit) || !batch.results.length) {
      break;
    }
  }
  return { results, count };
}
export async function allResources(
  path: string,
  params = new URLSearchParams(),
) {
  let resources: Resource[] = [];
  let included: Resource[] = [];
  for (let page = 1; ; page += 1) {
    const query = new URLSearchParams(params);
    query.set('page[number]', String(page));
    query.set('page[size]', '100');
    // eslint-disable-next-line no-await-in-loop
    const doc = await getDocument(path, query);
    resources = [...resources, ...(doc.data as Resource[])];
    included = [...included, ...(doc.included || [])];
    if (page >= doc.meta!.pagination.pages) {
      break;
    }
  }
  return { resources, included };
}

export async function getReassignableDeals(
  id: string,
  query: Query,
  selected: string[],
  signal?: AbortSignal,
): Promise<SelectionDeal[]> {
  const combined = new Set(selected);
  const eligible = new Map<string, SelectionDeal>();
  for (let page = 1; ; page += 1) {
    const params = dealQuery({ ...query, page, size: 100 });
    params.set('fields[Deal]', 'is_loan_officer_reassignable');
    // eslint-disable-next-line no-await-in-loop
    const doc = await getDocument(
      `/loan-officers/${id}/deals/`,
      params,
      signal,
    );
    (doc.data as Resource[])
      .filter((row) => row.attributes?.is_loan_officer_reassignable)
      .forEach((row) => {
        combined.add(row.id);
        if (combined.size > 500) {
          throw new Error('Select at most 500 deals at once.');
        }
        eligible.set(row.id, {
          id: row.id,
          is_loan_officer_reassignable: true,
        });
      });
    if (page >= doc.meta!.pagination.pages) {
      return Array.from(eligible.values());
    }
  }
}

export async function getReassignmentTargets(
  partnerId: string,
  query: { keyword: string; page: number },
  signal?: AbortSignal,
) {
  const params = new URLSearchParams({
    include: 'team',
    'filter[is_eligible_for_deal_assignment]': 'true',
    'page[number]': String(query.page),
    'page[size]': '20',
  });
  if (query.keyword) {
    params.set('filter[keyword]', query.keyword);
  }
  const doc = await getDocument(
    `/mortgage-partners/${partnerId}/loan-officers/`,
    params,
    signal,
  );
  return {
    officers: (doc.data as Resource[]).map((row) =>
      mapOfficer(row, doc.included),
    ),
    ...doc.meta!.pagination,
  };
}

export async function createBulkDealReassignment(
  targetId: string,
  dealIds: string[],
): Promise<Resource> {
  const response = await Axios.post(
    `${base}/loan-officer-bulk-deal-reassignments/`,
    {
      data: {
        type: 'LoanOfficerBulkDealReassignment',
        relationships: {
          target_loan_officer: { data: { type: 'LoanOfficer', id: targetId } },
          deals: { data: dealIds.map((id) => ({ type: 'Deal', id })) },
        },
      },
    },
    { headers: { 'Content-Type': 'application/vnd.api+json' } },
  );
  return response.data.data;
}
